<script setup>
import { computed, defineModel, onMounted, ref, useAttrs, watch } from "vue";
import { includeSubstring } from "@/lib/includes.js";

const props = defineProps({
  label: String,
  class: String,
  options: Array,
  onlyOptions: Boolean,
  defaultValue: String,
});
const attrs = useAttrs();
const value = defineModel();
const emit = defineEmits(["select"]);

onMounted(() => {
  if (props.defaultValue) {
    value.value = props.defaultValue;
  }
});

const refsOptions = ref([]);
const cursorOptions = ref();
const showOptions = ref(false);
function toggleShowOption(value) {
  showOptions.value = value ?? !showOptions.value;
}

const options = computed(() => {
  if (!Array.isArray(props.options) || typeof value.value !== "string") return;

  function filtration(option) {
    const handleValue = value.value ?? "";
    return (
      includeSubstring(option.title, handleValue) &&
      option.title !== handleValue
    );
  }

  return value.value ? props.options.filter(filtration) : props.options;
});

const isOptions = computed(
  () =>
    Array.isArray(options.value) &&
    options.value.length > 0 &&
    showOptions.value &&
    (attrs.type !== "radio" || attrs.type !== "checkbox"),
);

function handleOption(select) {
  if (error.value) error.value = undefined;
  value.value = select;
  showOptions.value = false;
  cursorOptions.value = undefined;
  emit("select", select);
}

function handleDown() {
  if (refsOptions.value.length === 0) return;
  if (typeof cursorOptions.value === "undefined") {
    cursorOptions.value = 0;
    return;
  }
  if (cursorOptions.value < refsOptions.value.length - 1) {
    cursorOptions.value += 1;
  } else {
    cursorOptions.value = 0;
  }
}

function handleUp() {
  if (refsOptions.value.length === 0) return;
  if (typeof cursorOptions.value === "undefined") {
    cursorOptions.value = refsOptions.value.length - 1;
    return;
  }
  if (cursorOptions.value !== 0) {
    cursorOptions.value -= 1;
  } else {
    cursorOptions.value = refsOptions.value.length - 1;
  }
}

function handleEnter() {
  if (typeof cursorOptions.value !== "undefined") {
    handleOption(options.value[cursorOptions.value].title);
  }
}

const error = ref();

function handleBlur() {
  toggleShowOption(false);

  if (!props.onlyOptions) return;

  const opts = props.options.map((option) => option.title);
  if (opts.includes(value.value)) {
    handleOption(value.value);
  } else {
    error.value = "Выберите вариант из выпадающего списка";
  }
}

function handleChange() {
  if (error.value) error.value = undefined;
}
</script>

<template>
  <label :class="[$style.wrapper, props.class]">
    <input
      :class="$style.input"
      v-bind="attrs"
      v-model.trim="value"
      @focus="toggleShowOption"
      @blur="handleBlur"
      @change="handleChange"
      @keydown.enter="handleEnter"
      @keydown.space="handleEnter"
      @keydown.up="handleUp"
      @keydown.down="handleDown"
    />
    {{ props.label }}

    <Transition name="fade">
      <ul :class="$style.options" v-if="isOptions">
        <li
          v-for="(item, index) of options"
          :key="item.id"
          :class="[$style.option, { [$style.focus]: index === cursorOptions }]"
          @click="handleOption(item.title)"
          role="button"
          ref="refsOptions"
        >
          {{ item.title }}
        </li>
      </ul>
    </Transition>
  </label>
  <p v-if="error" :class="$style.error">{{ error }}</p>
</template>

<style module>
:global(.fade-enter-to),
:global(.fade-leave-from) {
  opacity: 1;
}

:global(.fade-enter-from),
:global(.fade-leave-to) {
  opacity: 0;
}

.error {
  color: #ed1651;
  font-size: 0.75em;
}

.options {
  list-style: none;
  position: absolute;
  top: 45px;
  left: 0;
  right: 0;
  max-height: 235px;
  margin: 0 !important;
  padding: 2px 0 !important;
  background-color: #ffffff;
  border: 1px solid #afb3b4;
  border-radius: 4px;
  overflow: auto;
  transition: opacity 0.2s ease-in-out;
  z-index: 1;
}

.option {
  background-color: transparent;
  padding: 10px 16px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  text-overflow: ellipsis;
  overflow: hidden;
}

.option:hover,
.option.focus {
  background-color: #dedede80;
}

.wrapper {
  position: relative;
  display: block;
  font-size: 16px;
  line-height: 1.4;
  color: inherit;
  transition: color 0.2s ease-in-out;
}

.wrapper:has(.input[type="checkbox"]),
.wrapper:has(.input[type="radio"]) {
  cursor: pointer;
  user-select: none;
}

.wrapper:has(.input[type="checkbox"]):hover,
.wrapper:has(.input[type="radio"]):hover {
  color: var(--color-red-base);
}

.input {
  appearance: none;
  width: 100%;
  accent-color: var(--color-red-base);
  border: 1px solid #687f91;
  transition:
    border-color 0.2s ease-in-out,
    outline-color 0.2s ease-in-out;
}

.input::placeholder {
  color: #6c757d;
}

.input[type="text"],
.input[type="search"] {
  height: 40px;
  line-height: 40px;
  padding-left: 16px;
  padding-right: 16px;
  border-radius: 4px;
  border: 1px solid #687f91;
  outline: 2px solid transparent;
  box-shadow: 0px 1px 3px 0px #00000033 inset;
  cursor: text;
}

.input[type="text"]:focus,
.input[type="search"]:focus {
  border-color: var(--color-red-base);
  outline-color: var(--color-red-base);
}

.input[type="checkbox"],
.input[type="radio"] {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  float: left;
  width: calc(1em * 1.4);
  height: calc(1em * 1.4);
  margin: 0.05em 0.75em 0 0;
  box-shadow: inset 0.2em 0.2em 0 0 #e7eaef;
  border-radius: 1em;
  cursor: pointer;
}

.input[type="checkbox"]::after,
.input[type="radio"]::after {
  content: "";
  display: block;
  width: 0.8em;
  height: 0.8em;
  border-radius: 0.8em;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  background-color: var(--color-red-base);
}

.input[type="checkbox"]:checked:after,
.input[type="radio"]:checked:after {
  opacity: 1;
}

.wrapper:hover .input[type="checkbox"],
.wrapper:hover .input[type="radio"] {
  border-color: var(--color-red-base);
}
</style>
